import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { BrandConfigReportEndPointLogic } from "../../../../logic/Partners/DataIntegrations/BrandConfigReportEndPointLogic";
import {
  setBrandConfigReportLoader,
  setIsFetchedBrandConfigReport,
} from "../../../../features/brandConfigurationReportSlice";
import {
  selectUserToken,
  setDisplayErrorSnackBar,
} from "../../../../features/userSlice";

export const useBrandProperties = () => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const website_shared_uid = "test";

  const brandSharedUid = searchParams.get("brandSharedUid");
  const bkInterfaceSharedUid = searchParams.get("bkInterfaceSharedUid");

  const { brandPropertiesData, brandBackendRequirementsData } = useSelector(
    (state: any) => state.brandConfigurationReport,
  );

  // Extract and sort requirements
  const requirements = brandBackendRequirementsData?.[0]?.requirements || [];
  const sortedRequirements = [...requirements].sort(
    (a, b) => (a?.order || 0) - (b?.order || 0),
  );

  const keeperLabels = brandPropertiesData?.[0]?.keeper?.labels || [];
  const brandObjects = brandPropertiesData?.[0]?.objects || [];

  // Helper: Extract placeholder value for key type = pattern
  const extractPlaceholderValue = (
    defaultValue: string | any,
    fieldValue: string | any,
  ) => {
    try {
      const defaultObj = JSON.parse(defaultValue);
      const fieldObj = JSON.parse(fieldValue);

      // Find placeholder key
      const placeholderKey = Object.keys(defaultObj).find(
        (key) =>
          typeof defaultObj[key] === "string" &&
          defaultObj[key].includes("$$placeholder"),
      );

      if (!placeholderKey) return null;

      return {
        placeholderKey,
        extractedValue: fieldObj[placeholderKey],
      };
    } catch {
      return null;
    }
  };

  // Helper: Find matching data for requirement
  const findMatchingData = (requirement: any) => {
    const { property_target, key_name } = requirement;

    if (property_target === "keeper") {
      return keeperLabels.find((label: any) => label.label_name === key_name);
    }

    if (
      property_target === "brand_id" ||
      property_target === "raw_output_table_name"
    ) {
      return brandObjects.find(
        (obj: any) =>
          obj.field_type === key_name && obj.object_type === property_target,
      );
    }

    return null;
  };

  // Map requirements to formatted properties
  const formattedBrandProperties = sortedRequirements
    .map((requirement) => {
      const matchedfiledValue = findMatchingData(requirement);
      // if (!matchedData) return null;

      const {
        key_title,
        key_name,
        key_type,
        requirement_type,
        property_target,
        description,
        default_value,
      } = requirement;

      // Skip rows that have no value to display
      if (!key_name && !key_title && !key_type) return null;

      // Get value based on property target
      const label_value = matchedfiledValue?.label_value ?? null;
      const field_value = matchedfiledValue?.field_value ?? null;
      let value = null;
      let placeholder = null;
      let placeholderKey = null;

      if (key_type === "user_input" || key_type === "changeable") {
        value = label_value || field_value || default_value || "";
      }

      // Handle pattern type
      if (key_type === "pattern" && default_value !== null && field_value) {
        const extracted = extractPlaceholderValue(default_value, field_value);
        if (extracted) {
          value = extracted.extractedValue;
          placeholder = field_value;
          placeholderKey = extracted.placeholderKey;
        }
      }

      const baseProperty = {
        label: key_title,
        value,
        requirementType: requirement_type,
        helperText: ["required", "conditional_requirement"].includes(
          requirement_type,
        )
          ? `${key_title} is required.`
          : "",
        description,
        key_name,
        key_type,
        property_target,
      };

      // Add placeholder fields for pattern type
      return key_type === "pattern"
        ? { ...baseProperty, placeholder, placeholderKey }
        : baseProperty;
    })
    .filter(Boolean);

  // Fetch brand properties and requirements
  const getBrandPropertiesAndRequirements = async () => {
    if (!brandSharedUid || !bkInterfaceSharedUid || !oauthTokenHeader) {
      dispatch(
        setDisplayErrorSnackBar({
          message: "Missing required parameters.",
          type: "error",
        }),
      );
      return;
    }

    dispatch(setBrandConfigReportLoader(true));
    try {
      await Promise.all([
        BrandConfigReportEndPointLogic.getBrandConfigurationReport(
          oauthTokenHeader,
        ),
        BrandConfigReportEndPointLogic.getBrandBackendRequirements(
          oauthTokenHeader,
          bkInterfaceSharedUid,
        ),
        BrandConfigReportEndPointLogic.getBrandProperties(
          oauthTokenHeader,
          brandSharedUid,
        ),
      ]);
    } catch (err) {
      console.error("Error fetching brand data:", err);
      dispatch(
        setDisplayErrorSnackBar({
          message:
            "Failed to fetch brand configuration report. Please try again.",
          type: "error",
        }),
      );
    } finally {
      dispatch(setBrandConfigReportLoader(false));
    }
  };

  async function brandIntegrationProcess(brandId: any) {
    dispatch(setBrandConfigReportLoader(true));
    dispatch(setIsFetchedBrandConfigReport(false));
    try {
      const data = {
        brand_id: brandId,
        override_configuration: false,
      };

      const res =
        await BrandConfigReportEndPointLogic.postBrandConfigurationProcess(
          oauthTokenHeader,
          data,
        );

      if (res?.status === 200) {
        await Promise.all([
          BrandConfigReportEndPointLogic.getBrandConfigPIECharts(
            oauthTokenHeader,
            website_shared_uid,
          ),
          BrandConfigReportEndPointLogic.getBrandConfigurationReport(
            oauthTokenHeader,
          ),
        ]);
        const successMessage =
          res?.data?.message ||
          "Brand Integration Process has been successfully initiated.";

        dispatch(
          setDisplayErrorSnackBar({
            message: successMessage,
            type: "success",
          }),
        );
      } else {
        dispatch(
          setDisplayErrorSnackBar({
            message: "Failed to process brand integration.",
            type: "error",
          }),
        );
      }
    } catch (err) {
      dispatch(
        setDisplayErrorSnackBar({
          message: "Failed to process brand integration.",
          type: "error",
        }),
      );
    } finally {
      dispatch(setBrandConfigReportLoader(false));
      dispatch(setIsFetchedBrandConfigReport(true));
    }
  }

  return {
    formattedBrandProperties,
    getBrandPropertiesAndRequirements,
    brandIntegrationProcess,
  };
};
